import {
  Anchor,
  Button,
  Code,
  CopyButton,
  Group,
  Loader,
  Modal,
  SegmentedControl,
  Stack,
  Tabs,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import type { Share } from "../api/client";
import { endpoints } from "../api/client";
import { shareQuery, useNotifyError } from "../api/queries";
import { useMessages } from "../i18n/provider";

export function ShareDialog({
  resumeId,
  opened,
  onClose,
}: {
  resumeId: string;
  opened: boolean;
  onClose: () => void;
}) {
  const t = useMessages();
  const queryClient = useQueryClient();
  const notifyError = useNotifyError();
  const { data, isLoading } = useQuery({ ...shareQuery(resumeId), enabled: opened });

  const apply = async (next: Share) => {
    queryClient.setQueryData(["resume", resumeId, "share"], next);
    await queryClient.invalidateQueries({ queryKey: ["resumes"] });
  };

  const publish = useMutation({
    mutationFn: (visibility: "private" | "unlisted" | "public") =>
      visibility === "private"
        ? endpoints.unpublish(resumeId)
        : endpoints.publish(resumeId, visibility),
    onSuccess: (share) => apply(share),
    onError: notifyError,
  });

  const visibility = data?.visibility ?? "private";

  return (
    <Modal opened={opened} onClose={onClose} title={t("share.title")} size="lg">
      {isLoading || !data ? (
        <Group justify="center" py="xl">
          <Loader size="sm" color="gray" />
        </Group>
      ) : (
        <Tabs defaultValue="link">
          <Tabs.List mb="md">
            <Tabs.Tab value="link">{t("share.linkTab")}</Tabs.Tab>
            <Tabs.Tab value="embed">{t("share.embedTab")}</Tabs.Tab>
            <Tabs.Tab value="sdk">{t("share.sdkTab")}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="link">
            <Stack gap="md">
              <div>
                <Text size="sm" fw={500} mb={6}>
                  {t("share.visibility")}
                </Text>
                <SegmentedControl
                  fullWidth
                  value={visibility}
                  onChange={(value) => publish.mutate(value as "private" | "unlisted" | "public")}
                  data={[
                    { value: "private", label: t("share.private") },
                    { value: "unlisted", label: t("share.unlisted") },
                    { value: "public", label: t("share.public") },
                  ]}
                />
                <Text size="xs" c="dimmed" mt={8}>
                  {visibility === "private"
                    ? t("share.privateHint")
                    : visibility === "public"
                      ? t("share.publicHint")
                      : t("share.unlistedHint")}
                </Text>
              </div>

              {visibility === "private" || !data.publicUrl ? (
                <Button onClick={() => publish.mutate("unlisted")} loading={publish.isPending}>
                  {t("share.publish")}
                </Button>
              ) : (
                <Group align="flex-end" gap="xs" wrap="nowrap">
                  <TextInput
                    label={t("share.linkTab")}
                    value={data.publicUrl}
                    readOnly
                    style={{ flex: 1 }}
                  />
                  <CopyButton value={data.publicUrl}>
                    {({ copy, copied }) => (
                      <Button variant="light" onClick={copy} w={100}>
                        {copied ? t("common.copied") : t("common.copy")}
                      </Button>
                    )}
                  </CopyButton>
                  <Button
                    component="a"
                    href={data.publicUrl}
                    target="_blank"
                    variant="default"
                    className="yc-no-print"
                  >
                    {t("common.open")}
                  </Button>
                </Group>
              )}

              {visibility !== "private" ? (
                <Button
                  variant="subtle"
                  color="gray"
                  size="xs"
                  onClick={() => publish.mutate("private")}
                  loading={publish.isPending}
                  style={{ alignSelf: "flex-start" }}
                >
                  {t("share.unpublish")}
                </Button>
              ) : null}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="embed">
            {data.embedUrl && data.iframeHtml ? (
              <Stack gap="sm">
                <Textarea
                  value={data.iframeHtml}
                  readOnly
                  autosize
                  minRows={4}
                  styles={{ input: { fontFamily: "var(--yc-font-mono)", fontSize: 12 } }}
                />
                <Group>
                  <CopyButton value={data.iframeHtml}>
                    {({ copy, copied }) => (
                      <Button variant="light" onClick={copy}>
                        {copied ? t("common.copied") : t("common.copy")}
                      </Button>
                    )}
                  </CopyButton>
                  <Text size="sm" c="dimmed">
                    {t("share.pasteInto")}
                  </Text>
                </Group>
              </Stack>
            ) : (
              <Stack gap="sm">
                <Text size="sm" c="dimmed">
                  {t("share.privateHint")}
                </Text>
                <Button onClick={() => publish.mutate("unlisted")} loading={publish.isPending}>
                  {t("share.publish")}
                </Button>
              </Stack>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="sdk">
            <Stack gap="md">
              <div>
                <Text size="sm" fw={500} mb={6}>
                  {t("share.install")}
                </Text>
                <Group gap="xs" wrap="nowrap">
                  <Code block style={{ flex: 1 }} p="sm">
                    pnpm add @yourcv/sdk
                  </Code>
                  <CopyButton value="pnpm add @yourcv/sdk">
                    {({ copy, copied }) => (
                      <Button variant="light" onClick={copy} w={100}>
                        {copied ? t("common.copied") : t("common.copy")}
                      </Button>
                    )}
                  </CopyButton>
                </Group>
              </div>
              <Code block p="sm" style={{ fontFamily: "var(--yc-font-mono)", fontSize: 12 }}>
                {`import { createYourCvClient } from "@yourcv/sdk";

const cv = createYourCvClient({
  baseUrl: window.location.origin,
  apiKey: process.env.YOURCV_APP_PASSWORD!,
});

const share = await cv.resumes.share("${resumeId}");`}
              </Code>
              <Text size="sm" c="dimmed">
                {t("share.sdkHint")}{" "}
                <Anchor component={Link} to="/settings?tab=app-passwords" onClick={onClose}>
                  {t("share.appPasswords")}
                </Anchor>
              </Text>
            </Stack>
          </Tabs.Panel>
        </Tabs>
      )}
    </Modal>
  );
}
